/**
 * Single source of truth for plan metadata + feature matrix.
 *
 * Consumed by:
 *   - /pricing page (full comparison table)
 *   - Landing page pricing teaser
 *   - /brokers/:slug landing pricing teaser
 *   - Investor deck (Slide 6)
 *
 * When a new feature ships, add a row to FEATURE_MATRIX and tick the relevant
 * tier(s). The comparison page picks it up automatically.
 */

export const PLAN_TIERS = [
  {
    id: 'starter',
    name: 'Starter',
    price: 'Free',
    cadence: 'forever',
    promise: 'Get organised.',
    summary: 'The on-ramp. Track up to 5 assets in your encrypted vault.',
    cta: { label: 'Start free', to: '/register' },
    highlight: false,
  },
  {
    id: 'professional',
    name: 'Professional',
    price: '£29',
    cadence: 'per month',
    promise: 'Run your own wealth well.',
    summary: 'Everything you need to monitor, value and protect your portfolio yourself.',
    cta: { label: 'Choose Professional', to: '/register?tier=professional' },
    highlight: true,
  },
  {
    id: 'elite',
    name: 'Elite',
    price: '£79',
    cadence: 'per month',
    promise: 'Run it with your advisors.',
    summary: 'Bring your broker, wealth manager and family into the same private workflow.',
    cta: { label: 'Choose Elite', to: '/register?tier=elite' },
    highlight: false,
  },
];

/**
 * Each entry is one row in the comparison table.
 *   include: { starter, professional, elite }
 *     - true  -> ✓ (gold tick)
 *     - false -> — (muted dash)
 *     - string -> displayed verbatim ("Up to 5", "Monthly", etc.)
 */
export const FEATURE_MATRIX = [
  // ============== Track ==============
  {
    section: 'Track',
    rows: [
      {
        feature: 'Assets in your vault',
        sub: 'Cars, watches, art, jewellery, wine, handbags, property',
        include: { starter: 'Up to 5', professional: 'Unlimited', elite: 'Unlimited' },
      },
      {
        feature: 'Multi-currency support',
        include: { starter: true, professional: true, elite: true },
      },
      {
        feature: 'Encrypted document vault',
        sub: 'Receipts, valuations, policies, certificates',
        include: { starter: '50 MB', professional: '5 GB', elite: 'Unlimited' },
      },
      {
        feature: 'Asset valuation certificates (PDF)',
        sub: 'One-click branded certificate for any asset',
        include: { starter: false, professional: true, elite: true },
      },
    ],
  },

  // ============== Understand ==============
  {
    section: 'Understand',
    rows: [
      {
        feature: 'AI valuations',
        sub: 'GPT / Gemini-powered, refreshed automatically',
        include: { starter: 'Manual only', professional: 'Monthly', elite: 'Priority (weekly)' },
      },
      {
        feature: 'Portfolio Health Score',
        sub: 'Coverage · diversification · exposure',
        include: { starter: false, professional: true, elite: true },
      },
      {
        feature: 'Risk page',
        sub: 'Uninsured, underinsured, expiring, concentration',
        include: { starter: false, professional: true, elite: true },
      },
      {
        feature: 'Timeline view',
        sub: 'See your wealth grow over time',
        include: { starter: false, professional: true, elite: true },
      },
      {
        feature: 'Contextual AI Insights',
        sub: 'Action-oriented prompts based on YOUR portfolio',
        include: { starter: false, professional: true, elite: true },
      },
      {
        feature: 'Wealth Dossier PDF',
        sub: 'Full audit-ready report - KYC, risk, asset register',
        include: { starter: false, professional: true, elite: true },
      },
    ],
  },

  // ============== Act ==============
  {
    section: 'Act',
    rows: [
      {
        feature: 'Action ticket queue',
        sub: 'Open, in-progress, resolved - all tracked',
        include: { starter: false, professional: true, elite: true },
      },
      {
        feature: '"Fix this" email drafts',
        sub: 'Pre-filled outreach to your broker / insurer',
        include: { starter: false, professional: true, elite: true },
      },
      {
        feature: 'Advisor Hub',
        sub: 'Connect your broker - they see your inbox in real time',
        include: { starter: false, professional: false, elite: true },
      },
      {
        feature: 'In-app reply thread with advisors',
        sub: 'Bi-directional conversation, audit-trailed',
        include: { starter: false, professional: false, elite: true },
      },
      {
        feature: 'PII Secure Share',
        sub: 'Encrypted, one-time view, expiring KYC link',
        include: { starter: false, professional: false, elite: true },
      },
      {
        feature: 'Family / household seats',
        sub: 'Multiple users on one portfolio',
        include: { starter: false, professional: false, elite: '2 seats' },
      },
    ],
  },

  // ============== Trust & Support ==============
  {
    section: 'Trust & Support',
    rows: [
      {
        feature: 'Two-factor authentication',
        include: { starter: true, professional: true, elite: true },
      },
      {
        feature: 'Field-level PII encryption',
        sub: 'Tax / NI / Passport / DOB / Address',
        include: { starter: true, professional: true, elite: true },
      },
      {
        feature: 'Verified broker network',
        sub: 'FCA-cross-referenced',
        include: { starter: true, professional: true, elite: true },
      },
      {
        feature: 'Email support',
        include: { starter: 'Best effort', professional: '48h SLA', elite: 'Priority + concierge' },
      },
    ],
  },
];

/**
 * Partner tiers - shown on the public /pricing page as a separate row below
 * the consumer comparison table.
 */
export const PARTNER_TIERS = [
  {
    id: 'broker',
    name: 'Broker / Wealth Manager',
    price: '£150-500',
    cadence: 'per client / year',
    summary: 'Live client portfolios · proactive risk alerts · co-branded landing page · partner inbox of tickets and PII shares.',
    cta: { label: 'Apply to partner', to: '/brokers/apply' },
  },
  {
    id: 'strategic',
    name: 'Strategic partners',
    price: 'Per-introduction',
    cadence: '',
    summary: 'Insurers, auction houses, lenders, solicitors. Pre-qualified introductions at the moment of need.',
    cta: { label: 'Contact us', to: 'mailto:partners@assagart.co.uk' },
  },
];
